import { useState, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { 
  Search, 
  ListFilter, 
  Plus,
  RefreshCw,
  Eye,
  Receipt,
  X,
  Filter
} from "lucide-react";
import { getOrders, retryOrder } from "../../services/order_services";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { handleSetSearch } from "../../utils/searchDebouncing";
import ARHCustomDataTable from "../../components/dataTable/ARHCustomDataTable";
import CreateOrderModal from "./Components/CreateOrderModal";
import OrderDetailModal from "./Components/OrderDetailModal";
import OrderReceipt from "./Components/OrderReceipt";
import ARHAdvanceFilterModal from "../../components/application/date-picker/FilterModal";
import { ARHCustomDateFiler } from "../../components/application/date-picker/rangeCallernderCard";
import { useTranslation } from "react-i18next";

function Orders() {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.auth.user);
  const searchRef = useRef(null);
  const queryClient = useQueryClient();
  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
  
  const [filters, setFilters] = useState({
    search: "",
    limit: 10,
    page: 1,
    status: "",
    source: "",
    'createdAt[gte]': null,
    'createdAt[lte]': null,
  });
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["orders", filters],
    queryFn: () => getOrders(filters),
    keepPreviousData: true,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const retryOrderMutation = useMutation({
    mutationFn: (id) => retryOrder(id),
    onSuccess: (data, id) => {
      console.log("Order retry enqueued successfully");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (error, id) => {
      console.error("Failed to retry order:", error);
      const errorMessage = error.response?.data?.error || "Failed to retry order";
      console.error(errorMessage);
    }
  });

  const handleSearchChange = (value) => {
    setFilters(prev => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setFilters(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleRetryOrder = (id) => {
    retryOrderMutation.mutate(id);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailModalOpen(true);
  };

  const handleViewReceipt = (order) => {
    setSelectedOrder(order);
    setReceiptOpen(true);
  };

  const handleCreateSuccess = () => {
    setCreateModalOpen(false);
    refetch();
  };

  const handleDateRangeApply = (startDate, endDate) => {
    setFilters(prev => ({
      ...prev,
      'createdAt[gte]': startDate,
      'createdAt[lte]': endDate,
      page: 1,
    }));
    setIsDatePickerOpen(false);
  };

  const clearDateFilter = () => {
    setFilters(prev => {
      const newFilter = { ...prev };
      delete newFilter['createdAt[gte]'];
      delete newFilter['createdAt[lte]'];
      return newFilter;
    });
  };

  const formatDateRange = () => {
    if (!filters['createdAt[gte]'] || !filters['createdAt[lte]']) return null;
    const start = new Date(filters['createdAt[gte]']);
    const end = new Date(filters['createdAt[lte]']);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const filterConfig = useMemo(() => [
    {
      key: 'status',
      label: t('orders.filters.status'),
      type: 'dropdown',
      options: [
        { value: 'pending', label: t('orders.status.pending') },
        { value: 'inprocess', label: t('orders.status.inprocess') },
        { value: 'succeeded', label: t('orders.status.success') },
        { value: 'failed', label: t('orders.status.failed') },
      ],
      multiSelect: false,
    },
  
    {
      key: 'amount',
      label: t('orders.filters.amountRange'),
      type: 'range',
      min: 0,
      max: 100000,
      unit: 'AFN',
    }
  ], [t]);

  const initialAdvanceValues = useMemo(() => {
    return {
      status: filters.status || '',
      source: filters.source || '',
      amount: {
        min: filters['amount[gte]'] || 0,
        max: filters['amount[lte]'] || 100000,
      }
    };
  }, [filters]);

  const handleApplyAdvanceFilters = (filterValues) => {
    setFilters(prev => {
      const newFilter = { ...prev, page: 1 };
      
      if (filterValues.status) {
        newFilter.status = filterValues.status;
      } else {
        delete newFilter.status;
      }
      
      if (filterValues.source) {
        newFilter.source = filterValues.source;
      } else {
        delete newFilter.source;
      }
      
      if (filterValues.amount) {
        if (filterValues.amount.min > 0) {
          newFilter['amount[gte]'] = filterValues.amount.min;
        } else {
          delete newFilter['amount[gte]'];
        }
        
        if (filterValues.amount.max < 100000) {
          newFilter['amount[lte]'] = filterValues.amount.max;
        } else {
          delete newFilter['amount[lte]'];
        }
      } else {
        delete newFilter['amount[gte]'];
        delete newFilter['amount[lte]'];
      }
      
      return newFilter;
    });
    
    setIsAdvanceFilterOpen(false);
  };

  const clearAllFilters = () => {
    setFilters({
      search: "",
      limit: 10,
      page: 1,
      status: "",
      source: "",
      'createdAt[gte]': null,
      'createdAt[lte]': null,
    });
  };

  const isAnyFilterApplied = useMemo(() => {
    return (
      filters.search || 
      filters['createdAt[gte]'] || 
      filters['createdAt[lte]'] || 
      filters.status || 
      filters.source || 
      filters['amount[gte]'] || 
      filters['amount[lte]']
    );
  }, [filters]);

  const getStatusColor = (status) => {
    switch (status) {
      case "succeeded":
      case "success":
      case "completed":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "pending":
      case "inprocess":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const columns = useMemo(() => [
    { 
      key: "txnNumber", 
      label: t('orders.columns.transactionId')
    },
    { 
      key: "receiver", 
      label: t('orders.columns.receiver')
    },
    { 
      key: "amount", 
      label: t('orders.columns.amount'),
      render: (item) => `${Number(item?.amount || 0).toFixed(2)} ${item?.currency || "AFN"}`
    },
    {
      key: "type",
      label: t('orders.columns.type'),
      render: (item) => (
        <span className="capitalize">
          {item?.type?.replace("_", " ") || "-"}
        </span>
      )
    },
    {
      key: "status",
      label: t('orders.columns.status'),
      render: (item) => (
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item?.status)}`}>
          {t(`orders.status.${item?.status}`) || item?.status || "-"}
        </div>
      )
    },
    {
      key: "createdAt",
      label: t('orders.columns.createdAt'),
      render: (item) => item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"
    },
    {
      key: "actions",
      label: t('orders.columns.actions'),
      render: (item) => (
        <div className="flex space-x-2">
          {/* <button
            onClick={() => handleViewDetails(item)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title={t('orders.actions.viewDetails')}
          >
            <Eye size={16} />
          </button> */}
          
          <button
            onClick={() => handleViewReceipt(item)}
            className="text-green-600 hover:text-green-800 p-1"
            title={t('orders.actions.viewReceipt')}
          >
            <Receipt size={16} />
          </button>
          
          {/* {item?.status === "failed" && (
            <button
              onClick={() => handleRetryOrder(item.id)}
              className="text-orange-600 hover:text-orange-800 p-1"
              title={t('orders.actions.retry')}
              disabled={retryOrderMutation.isLoading}
            >
              <RefreshCw 
                size={16} 
                className={retryOrderMutation.isLoading ? "animate-spin" : ""}
              />
            </button>
          )} */}
        </div>
      )
    }
  ], [t, retryOrderMutation.isLoading]);

  const customEmptyState = useMemo(() => (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img 
        src="/images/Search.png" 
        alt={t('common.noDataFound')}
        className="w-[170px] h-[170px]"
      />
      <p className="mt-2 text-gray-500">{t('orders.noOrdersFound')}</p>
    </div>
  ), [t]);

  const customErrorState = useMemo(() => (
    <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
      <img 
        src="/images/Search.png" 
        alt={t('common.error')}
        className="w-[170px] h-[170px]"
      />
      <div className="text-sm text-red-600 mb-2">
        {error?.message || t('orders.loadFailed')}
      </div>
      <button
        onClick={() => refetch()}
        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
      >
        {t('common.retry')}
      </button>
    </div>
  ), [error, t, refetch]);

  return (
    <div className="w-full px-5 py-4 min-h-screen bg-gray-50 dark:bg-gray-900">
      <Elements stripe={stripePromise}>
        <CreateOrderModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      </Elements>

      <OrderDetailModal
        order={selectedOrder}
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedOrder(null);
        }}
      />
      
      <OrderReceipt
        order={selectedOrder}
        open={receiptOpen}
        onClose={() => {
          setReceiptOpen(false);
          setSelectedOrder(null);
        }}
      />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          {t('orders.title')}
        </h1>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="bg-[#CD0C02] px-4 py-2 rounded-md text-white flex items-center hover:bg-[#a60a01] transition-colors"
        >
          <Plus size={18} className="mr-2" />
          {t('orders.newOrder')}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-grow max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              ref={searchRef}
              onChange={(e) => handleSetSearch(handleSearchChange, e.target.value, searchRef)}
              type="text"
              placeholder={t('orders.searchPlaceholder')}
              defaultValue={filters.search}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setIsDatePickerOpen(true)}
              className="max-w-[250px] cursor-pointer w-full md:w-auto border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 h-[42px] flex items-center justify-center gap-2 bg-transparent dark:text-white relative hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ListFilter size={16} />
              <span className="truncate">
                {filters['createdAt[gte]'] && filters['createdAt[lte]'] 
                  ? formatDateRange()
                  : t('orders.dateFilter')}
              </span>
              
              {filters['createdAt[gte]'] && filters['createdAt[lte]'] && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    clearDateFilter();
                  }}
                  className="absolute right-2 text-gray-500 hover:text-red-500"
                >
                  <X size={16} />
                </button>
              )}
            </button>
            
            <button
              onClick={() => setIsAdvanceFilterOpen(true)}
              className="max-w-[150px] cursor-pointer w-full md:w-auto border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 h-[42px] flex items-center justify-center gap-2 bg-transparent dark:text-white relative hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Filter size={16} />
              {t('orders.filters.title')}
              {isAnyFilterApplied && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                  <span className="text-white text-[10px]">!</span>
                </span>
              )}
            </button>
            
            {isAnyFilterApplied && (
              <button
                onClick={clearAllFilters}
                className="max-w-[120px] cursor-pointer w-full md:w-auto border border-red-500 rounded-md px-3 py-2 h-[42px] flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-sm"
              >
                {t('orders.clearAll')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <ARHCustomDataTable
          data={data?.data || []}
          columns={columns}
          meta={data?.meta || {
            page: filters.page,
            pages: 1,
            total: 0,
            limit: filters.limit
          }}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={refetch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          searchQuery={filters.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
          rowClassName="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
        />
      </div>

      {isDatePickerOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setIsDatePickerOpen(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl max-w-full overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            <ARHCustomDateFiler
              onApply={handleDateRangeApply} 
              onClose={() => setIsDatePickerOpen(false)} 
            />
          </div>
        </div>
      )}
      
      {isAdvanceFilterOpen && (
        <ARHAdvanceFilterModal
          isOpen={isAdvanceFilterOpen}
          onClose={() => setIsAdvanceFilterOpen(false)}
          onApply={handleApplyAdvanceFilters}
          filterConfig={filterConfig}
          initialValues={initialAdvanceValues}
        />
      )}
    </div>
  );
}

export default Orders;